import type * as React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';

import { cn } from '../../styles/lib/utils';
import { ComboBox, type ComboBoxOption } from '../ComboBox';
import { FormControl, FormField, type FormFieldProps, FormItem, FormMessage } from './Form';

type FormComboBoxProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  FormFieldProps<TFieldValues, TName>,
  'render' | 'disabled'
> & {
  label: string;
  options: ComboBoxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  tooltipText?: React.ReactNode;
  isClearable?: boolean;
  disabled?: boolean;
  secondaryAction?: React.ReactNode;
  className?: string;
  /** For hosts the popover's default `z-50` does not clear. */
  contentClassName?: string;
  triggerClassName?: string;
  'data-testid'?: string;
  labelTestId?: string;
};

/**
 * react-hook-form field around `ComboBox`, for a long or data-driven list, or options with a description line.
 * Short, fixed lists use `FormSelect`; see the `ComboBox` doc block for the rule.
 */
function FormComboBox<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  label,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found',
  tooltipText,
  isClearable = false,
  disabled,
  secondaryAction,
  className,
  contentClassName,
  triggerClassName,
  'data-testid': testId,
  labelTestId,
  ...fieldProps
}: FormComboBoxProps<TFieldValues, TName>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field }) => (
        <FormItem className={className}>
          <FormControl>
            <ComboBox
              label={label}
              isRequired={fieldProps.isRequired}
              tooltipText={tooltipText}
              secondaryAction={secondaryAction}
              labelTestId={labelTestId}
              options={options}
              value={field.value ?? ''}
              onValueChange={(next) => field.onChange(next === '' ? null : next)}
              onBlur={field.onBlur}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              emptyText={emptyText}
              isClearable={isClearable}
              allowDeselect={false}
              disabled={disabled}
              className={contentClassName}
              triggerClassName={cn('w-full', triggerClassName)}
              data-testid={testId}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { FormComboBox };
export type { FormComboBoxProps };
